import React from 'react';
import { Users, Target, Heart, Sparkles, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Authenticity',
      description: 'Real stories and genuine conversations about topics our culture doesn\'t always discuss openly.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'A space where women support women, no judgment - consejos de mujeres a mujeres.',
    },
    {
      icon: Sparkles,
      title: 'Empowerment',
      description: 'Inspiring and building each other up to live our best lives as working mamis.',
    },
    {
      icon: Target,
      title: 'Identity',
      description: 'Embracing our uniqueness as bilingual (spanglish speaking) millennial Latinas.',
    },
  ];

  const hosts = [
    {
      name: 'Jatnna Abreu',
      role: 'Co-Host & Co-Founder',
      bio: 'Born and raised in the Dominican Republic, moved to Grand Rapids at 14. Bachelor\'s in Business Administration (GVSU), Master\'s in Organizational Development (WMU). Recognized as one of Grand Rapids Business Journal\'s 40 under 40 Business Leaders. Meetings & Events Supervisor. Loves spending time with her husband, son and daughter. Enjoys a little bit of retail therapy, traveling, reading women empowering books, home projects, and watching her favorite shows.',
      image: 'https://customer-assets.emergentagent.com/job_af4289ab-e836-496b-954f-6cfb1ffaac19/artifacts/aybgf3yp_444be080-59f0-4ca1-a4b9-a428cd567ae0.jpeg',
    },
    {
      name: 'Rosibel Madera Vialet',
      role: 'Co-Host & Co-Founder',
      bio: 'Born in Manhattan, New York, moved to Grand Rapids at 7. Bachelor\'s in Healthcare Administration and MBA in Marketing (Davenport University). Studied abroad in China for 3 weeks. Grew up in a family specialty grocery store since age eight which shaped who she is today. Helped innovate her parents\' business (20+ years). Married with two children, Maximus and Ernisbel. Enjoys helping talented people turn their skill sets into Dinero!',
      image: 'https://customer-assets.emergentagent.com/job_af4289ab-e836-496b-954f-6cfb1ffaac19/artifacts/f1vb9ju8_85b2e94d-abd9-4280-8297-6f6d5f1d826e.jpeg',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            About Us
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            We are two amigas on a journey to figure out how to do it all as millennial Latinas 
            and working mamis, one honest conversation at a time.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Our <span style={{ color: '#b76e79' }}>Story</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We created this podcast to empower Latinas to speak up, prioritize self-care, and 
                fully embrace who they are. As bilingual Spanish-speaking women, we celebrate our 
                unique identities while sharing personal stories and real, unfiltered conversations.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Together with our guests, we dive into topics our culture—and many of us as women—don't 
                always feel comfortable talking about, but deeply need to. We're two best friends 
                navigating life as moms, eldest daughters, wives, and professionals, learning as we go.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Join us on this journey of growth, honesty, and sisterhood.
              </p>
            </div>
            <div className="relative bg-gray-100 rounded-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_af4289ab-e836-496b-954f-6cfb1ffaac19/artifacts/m8gka077_d365c705-6d67-49e7-9088-4fc5792c7d5f.jpeg"
                alt="Jatnna and Rosibel"
                className="rounded-lg shadow-2xl w-full h-[500px] object-contain"
              />
            </div>
          </div>

          {/* Achievements */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">
              Awards & <span style={{ color: '#b76e79' }}>Recognition</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <a 
                href="https://goodpods.com/podcasts/dos-bold-latinas-530351" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full">
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ backgroundColor: '#b76e79' }}
                    >
                      <Award size={40} className="text-white" />
                    </div>
                    <span className="px-4 py-1 rounded-full text-sm font-semibold text-white mb-4 inline-block" style={{ backgroundColor: '#b76e79' }}>
                      #1 Ranked
                    </span>
                    <h3 className="text-2xl font-bold mb-3">Goodpods Top 100</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Ranked #1 in Top 100 Identity All-Time Chart on Goodpods. Thank you to our amazing community!
                    </p>
                  </CardContent>
                </Card>
              </a>

              <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full">
                <CardContent className="p-8 text-center">
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: '#b76e79' }}
                  >
                    <TrendingUp size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">40 Under 40</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Jatnna Abreu was recognized by Grand Rapids Business Journal as one of their 40 Under 40 Business Leaders
                  </p>
                  <span className="text-sm text-gray-500 font-medium mt-4 block">Grand Rapids Business Journal</span>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Values */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">
              Our <span style={{ color: '#b76e79' }}>Values</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card 
                    key={index} 
                    className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none"
                  >
                    <CardContent className="p-8">
                      <div 
                        className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                        style={{ backgroundColor: '#b76e79' }}
                      >
                        <Icon size={32} className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Hosts */}
          <div>
            <h2 className="text-4xl font-bold text-center mb-4">
              Meet Your <span style={{ color: '#b76e79' }}>Hosts</span>
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
              The dynamic duo behind Dos Bold Latinas podcast - that's us!
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {hosts.map((host, index) => (
                <Card 
                  key={index} 
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-none"
                >
                  <div className="relative h-96 overflow-hidden bg-gray-100">
                    <img
                      src={host.image}
                      alt={host.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold mb-2">{host.name}</h3>
                    <p className="text-lg mb-4" style={{ color: '#b76e79' }}>
                      {host.role}
                    </p>
                    <p className="text-gray-600 leading-relaxed">{host.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join Our Journey
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Be part of our community - where we share, support, and inspire each other 
            to live our best lives as millennial Latinas and working mamis.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/podcast">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#b76e79' }}
              >
                Listen Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 bg-white/10 text-white border-white hover:bg-white hover:text-black transition-all duration-300 hover:scale-105"
              >
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;